import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import {
    getLanguages,
    createLanguage,
    updateLanguage,
    deleteLanguage,
    clearError
} from '../../redux/translationSlice';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';

const LanguageMaster = () => {
    const { t } = useTranslation('languagemaster');
    const dispatch = useDispatch();
    const { 
        languages, 
        languageLoading, 
        languageCreateLoading, 
        languageUpdateLoading, 
        languagePagination, 
        error 
    } = useSelector((state) => state.translation);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: ''
    });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // Debounce search query
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch languages on component mount and when search/page changes
    useEffect(() => {
        if (!searchError) {
            dispatch(getLanguages({ 
                page: currentPage, 
                limit: itemsPerPage, 
                search: debouncedSearchQuery 
            }));
        }
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, searchError]);

    // Validate search input
    const validateSearchInput = (value) => {
        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        
        // Check length
        if (sanitized.length > 50) {
            return t('languagemaster.search_exceed_limit', 'Search cannot exceed 50 characters');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('languagemaster.search_only_whitespace', 'Search cannot contain only whitespace');
        }
        
        return '';
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'name') {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');
            // Enforce max length
            const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;
            
            setFormData(prev => ({
                ...prev,
                [name]: finalValue
            }));
            
            // Validate
            if (!finalValue.trim()) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.name_required', 'Language name is required') }));
            } else if (/^\s+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.name_only_whitespace', 'Language name cannot contain only whitespace') }));
            } else if (finalValue.trim().length < 2) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.name_min_length', 'Language name must be at least 2 characters') }));
            } else if (finalValue.length > 50) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.name_max_length', 'Language name cannot exceed 50 characters') }));
            } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.name_invalid_chars', 'Language name can only contain letters, spaces, hyphens, and apostrophes') }));
            } else {
                setErrors(prev => ({ ...prev, [name]: '' }));
            }
        } else if (name === 'code') {
            // Sanitize input - remove dangerous characters and convert to lowercase
            const sanitized = value.replace(/[<>]/g, '').toLowerCase();
            // Only allow lowercase letters and numbers
            const alphanumeric = sanitized.replace(/[^a-z0-9]/g, '');
            // Enforce max length
            const finalValue = alphanumeric.length > 10 ? alphanumeric.substring(0, 10) : alphanumeric;
            
            setFormData(prev => ({
                ...prev,
                [name]: finalValue
            }));
            
            // Validate
            if (!finalValue.trim()) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.code_required', 'Language code is required') }));
            } else if (/^\s+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.code_only_whitespace', 'Language code cannot contain only whitespace') }));
            } else if (finalValue.length < 2) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.code_min_length', 'Language code must be at least 2 characters') }));
            } else if (finalValue.length > 10) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.code_max_length', 'Language code cannot exceed 10 characters') }));
            } else if (!/^[a-z0-9]+$/.test(finalValue)) {
                setErrors(prev => ({ ...prev, [name]: t('languagemaster.code_invalid_chars', 'Language code can only contain lowercase letters and numbers') }));
            } else {
                setErrors(prev => ({ ...prev, [name]: '' }));
            }
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        // Validate name
        if (!formData.name.trim()) {
            newErrors.name = t('languagemaster.name_required', 'Language name is required');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('languagemaster.name_only_whitespace', 'Language name cannot contain only whitespace');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('languagemaster.name_min_length', 'Language name must be at least 2 characters');
        } else if (formData.name.length > 50) {
            newErrors.name = t('languagemaster.name_max_length', 'Language name cannot exceed 50 characters');
        } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(formData.name)) {
            newErrors.name = t('languagemaster.name_invalid_chars', 'Language name can only contain letters, spaces, hyphens, and apostrophes');
        }

        // Validate code
        if (!formData.code.trim()) {
            newErrors.code = t('languagemaster.code_required', 'Language code is required');
        } else if (/^\s+$/.test(formData.code)) {
            newErrors.code = t('languagemaster.code_only_whitespace', 'Language code cannot contain only whitespace');
        } else if (formData.code.length < 2) {
            newErrors.code = t('languagemaster.code_min_length', 'Language code must be at least 2 characters');
        } else if (formData.code.length > 10) {
            newErrors.code = t('languagemaster.code_max_length', 'Language code cannot exceed 10 characters');
        } else if (!/^[a-z0-9]+$/.test(formData.code)) {
            newErrors.code = t('languagemaster.code_invalid_chars', 'Language code can only contain lowercase letters and numbers');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle add new language
    const handleAdd = () => {
        setEditingLanguage(null);
        setFormData({
            name: '',
            code: ''
        });
        setErrors({});
        setShowModal(true);
    };

    // Handle edit language
    const handleEdit = (language) => {
        setEditingLanguage(language);
        setFormData({
            name: language.name || '',
            code: language.code || ''
        });
        setErrors({});
        setShowModal(true);
    };

    // Handle delete language
    const handleDelete = async (language) => {
        const languageId = language.languageId || language._id;

        const refreshList = () => dispatch(getLanguages({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery
        }));

        // Perform the delete request; on 409 (in use) offer a force-delete
        // confirmation that reassigns referencing rows to English ('en').
        const runDelete = async ({ force }) => {
            const action = await dispatch(deleteLanguage({ languageId, force }));
            if (action.type.endsWith('/rejected')) {
                dispatch(clearError());
            }

            if (action.type.endsWith('/fulfilled')) {
                toast.success(force
                    ? t('languagemaster.force_deleted_success', 'Language deleted. Assigned doctors/patients/users have been moved to English (en).')
                    : t('languagemaster.deleted_success', 'Language has been deleted successfully.'));
                refreshList();
                return;
            }

            const payload = action.payload || {};

            if ((payload.inUse || payload.status === 409) && !force) {
                const forceConfirm = await Swal.fire({
                    title: t('languagemaster.cannot_delete_title', 'Cannot delete language'),
                    html: `${payload.error || t('languagemaster.in_use_text', 'This language is assigned to one or more doctors/patients/users.')}
                        <br/><br/>
                        <strong>${t('languagemaster.force_delete_prompt', 'Force delete and reassign them to English (en)?')}</strong>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#4ABDBD',
                    confirmButtonText: t('languagemaster.force_delete', 'Yes, force delete'),
                    cancelButtonText: t('languagemaster.cancel', 'Cancel')
                });

                if (forceConfirm.isConfirmed) {
                    await runDelete({ force: true });
                }
                return;
            }

            throw new Error(payload.error || t('languagemaster.delete_failed', 'Failed to delete language'));
        };

        const result = await Swal.fire({
            title: t('languagemaster.delete_title', 'Delete Language?'),
            text: t('languagemaster.delete_confirm_text', `Are you sure you want to delete "${language.name}"? This action cannot be undone.`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('languagemaster.yes_delete', 'Yes, delete it!'),
            cancelButtonText: t('languagemaster.cancel', 'Cancel')
        });

        if (result.isConfirmed) {
            try {
                await runDelete({ force: false });
            } catch (error) {
                toast.warn(error?.error || error?.message || t('languagemaster.delete_failed', 'Failed to delete language.'));
            }
        }
    };

    // Handle save (create or update)
    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            let result;
            if (editingLanguage) {
                result = await dispatch(updateLanguage({
                    languageId: editingLanguage.languageId || editingLanguage._id,
                    name: formData.name,
                    code: formData.code
                }));
            } else {
                result = await dispatch(createLanguage({
                    name: formData.name,
                    code: formData.code
                }));
            }

            if (result.type.endsWith('/fulfilled')) {
                toast.success(editingLanguage
                        ? t('languagemaster.updated_success', 'Language has been updated successfully.')
                        : t('languagemaster.created_success', 'Language has been created successfully.'));
                setCurrentPage(1);
                dispatch(getLanguages({ 
                    page: 1, 
                    limit: itemsPerPage, 
                    search: debouncedSearchQuery 
                }));
            } else {
                throw new Error(result.payload?.error || (editingLanguage ? t('languagemaster.update_failed', 'Failed to update language.') : t('languagemaster.create_failed', 'Failed to create language.')));
            }

            setShowModal(false);
            setFormData({ name: '', code: '' });
            setEditingLanguage(null);
            setErrors({});
        } catch (error) {
            toast.warn(error?.error || error?.message || (editingLanguage ? t('languagemaster.update_failed', 'Failed to update language.') : t('languagemaster.create_failed', 'Failed to create language.')));
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ name: '', code: '' });
        setEditingLanguage(null);
        setErrors({});
    };

    // Handle page change
    const handlePageChange = (newPage, newItemsPerPage) => {
        if (newItemsPerPage !== undefined && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1); // Reset to first page when items per page changes
        } else {
            setCurrentPage(newPage);
        }
    };

    // Filter languages for display (client-side filter if needed)
    const filteredLanguages = languages || [];

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures (delete/update/create) shouldn't
    // hijack the whole page; they're surfaced via toast/Swal in their handlers.
    const hasNoLanguages = !languages || languages.length === 0;
    if (error && hasNoLanguages && !languageLoading && error?.status >= 500) {
        return <ErrorPage error={error} pageName={t('languagemaster.page_name', 'Language Management')} />;
    }

    return (
        <div className="w-full" data-testid="language-master-container">
            {/* Main Container */}
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full" data-testid="language-master-card">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4" data-testid="language-master-header">
                    <div>
                        <h1 className="text-2xl sm:text-3xl pb-2 mr-4 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="language-master-title">
                            {t('languagemaster.page_name', 'Language Management')}
                        </h1>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                        data-testid="add-language-button"
                    >
                        <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" data-testid="add-language-icon" />
                        {t('languagemaster.add_language', 'Add Language')}
                    </button>
                </div>

                {/* Search Bar */}
                <div className="mb-5" data-testid="language-search-section">
                    <div className="relative group">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} data-testid="language-search-icon" />
                            <input
                                type="text"
                                placeholder={t('languagemaster.search_placeholder', 'Search languages by name...')}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                maxLength={50}
                                className={`w-1/3 pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${
                                    searchError
                                        ? 'border-red-300 bg-red-50'
                                        : 'border-gray-200'
                                }`}
                                data-testid="language-search-input"
                            />
                            {searchError && (
                                <p className="mt-1 text-xs text-red-500" data-testid="language-search-error">{searchError}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Languages Grid */}
                {(!languageLoading && filteredLanguages.length === 0) ? (
                    <div className="text-center py-16 sm:py-20" data-testid="language-empty-state">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6" data-testid="language-empty-icon-container">
                            <Search size={48} className="text-gray-400" data-testid="language-empty-icon" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="language-empty-title">
                            {searchQuery ? t('languagemaster.no_languages_found', 'No languages found') : t('languagemaster.no_languages_yet', 'No languages yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" data-testid="language-empty-description">
                            {searchQuery
                                ? t('languagemaster.adjust_search', 'Try adjusting your search terms to find what you\'re looking for.')
                                : t('languagemaster.get_started', 'Get started by creating your first language.')}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="language-create-first-button"
                            >
                                <Plus size={20} data-testid="language-create-first-icon" />
                                {t('languagemaster.create_first', 'Create your first language')}
                            </button>
                        )}
                    </div>
                ) : languageLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="language-loading-container">
                        <CommonLoader />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4" data-testid="language-grid">
                            {filteredLanguages.map((language) => (
                                <div
                                    key={language.languageId || language._id || language.id}
                                    className="group relative bg-white border border-gray-200 rounded-lg p-4 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 overflow-hidden"
                                    data-testid={`language-card-${language.languageId || language._id || language.id}`}
                                >
                                    {/* Gradient Accent */}
                                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

                                    {/* Content */}
                                    <div className="mb-3">
                                        <h3 className="text-base font-bold text-gray-800 line-clamp-1 mb-2 group-hover:text-[#4ABDBD] transition-colors" data-testid={`language-name-${language.languageId || language._id || language.id}`}>
                                            {language.name}
                                        </h3>
                                        <p className="text-xs text-gray-600" data-testid={`language-code-row-${language.languageId || language._id || language.id}`}>
                                            {t('languagemaster.code_label', 'Code')}: <span className="font-semibold" data-testid={`language-code-${language.languageId || language._id || language.id}`}>{language.code}</span>
                                        </p>
                                        {/* <p className="text-xs text-gray-500 mt-1">
                                            ID: {language.languageId || language._id}
                                        </p> */}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100" data-testid={`language-actions-${language.languageId || language._id || language.id}`}>
                                        <button
                                            onClick={() => handleEdit(language)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                            title={t('languagemaster.edit')}
                                            data-testid={`language-edit-button-${language.languageId || language._id || language.id}`}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Edit2 className="h-4 w-4 relative z-10" data-testid={`language-edit-icon-${language.languageId || language._id || language.id}`} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(language)}
                                            className="group/btn text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                            title={t('languagemaster.delete')}
                                            data-testid={`language-delete-button-${language.languageId || language._id || language.id}`}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Trash2 className="h-4 w-4 relative z-10" data-testid={`language-delete-icon-${language.languageId || language._id || language.id}`} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Pagination */}
                        {languagePagination.total > 0 && (
                            <Pagination
                                totalItems={languagePagination.total}
                                currentPage={languagePagination.page || currentPage}
                                itemsPerPage={languagePagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={filteredLanguages.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="language-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="language-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between" data-testid="language-modal-header">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="language-modal-title">
                                    {editingLanguage ? t('languagemaster.edit_language', 'Edit Language') : t('languagemaster.add_new_language', 'Add New Language')}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="language-modal-close-button"
                                >
                                    <X size={24} data-testid="language-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6" data-testid="language-modal-body">
                                {/* Language Name */}
                                <div data-testid="language-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="language-name-label">
                                        {t('languagemaster.language_name_label', 'Language Name')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        maxLength={50}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${
                                            errors.name
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                        }`}
                                        placeholder={t('languagemaster.name_placeholder', 'e.g., English, French, Spanish')}
                                        data-testid="language-name-input"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="language-name-error">{errors.name}</p>
                                    )}
                                </div>

                                {/* Language Code */}
                                <div data-testid="language-code-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="language-code-input-label">
                                        {t('languagemaster.language_code_label', 'Language Code')} * <span className="text-gray-400">({t('languagemaster.max_10_chars', 'Max 10 characters')})</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        maxLength={10}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${
                                            errors.code
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300'
                                        }`}
                                        placeholder={t('languagemaster.code_placeholder', 'e.g., en, fr, es')}
                                        data-testid="language-code-input"
                                    />
                                    {errors.code && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="language-code-error">{errors.code}</p>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3" data-testid="language-modal-footer">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={languageCreateLoading || languageUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="language-modal-cancel-button"
                                >
                                    {t('languagemaster.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={languageCreateLoading || languageUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="language-modal-save-button"
                                >
                                    {(languageCreateLoading || languageUpdateLoading) && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="language-modal-save-loading"></span>
                                    )}
                                    {languageCreateLoading || languageUpdateLoading
                                        ? (editingLanguage ? t('languagemaster.updating', 'Updating...') : t('languagemaster.creating', 'Creating...'))
                                        : (editingLanguage ? t('languagemaster.update', 'Update') : t('languagemaster.create', 'Create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LanguageMaster;

